import { NavLink, useLocation } from "react-router-dom";
import { useJourneyAuth } from "../auth/AuthContext";
import { AGENT_LOGIN_URL, BROKER_LOGIN_URL, WINDOWS_APP_URL, appUrl, deskUrl } from "../config";
import { withFilmQuery } from "@tour/film.js";
import MembershipActivationGate from "./MembershipActivationGate";

const PRIMARY = [
  { to: "/", label: "Home", end: true },
  { to: "/why", label: "About" },
  { to: "/manuals", label: "Manuals" },
  { to: "/support", label: "Support" },
  { to: "/community", label: "Community" },
  { to: "/contact", label: "Contact" },
];

const FOOTER = {
  About: [
    { to: "/why", label: "Why DreamHomeJourney" },
    { to: "/why-rechecklist", label: "Why REChecklist" },
    { to: "/benefits/buyers", label: "For buyers" },
    { to: "/benefits/sellers", label: "For sellers" },
    { to: "/benefits/agents", label: "For agents" },
    { to: "/benefits/brokers", label: "For brokers" },
    { to: "/testimonials", label: "Testimonials" },
  ],
  Learn: [
    { to: "/manuals", label: "User manuals" },
    { to: "/support/tour", label: "Guided Tour" },
    { to: "/faq", label: "FAQ" },
    { to: "/pricing", label: "Pricing" },
  ],
  Connect: [
    { to: "/support", label: "Support" },
    { to: "/vendors", label: "Vendors" },
    { to: "/events", label: "Events" },
    { to: "/calendar", label: "Calendar" },
    { to: "/contact", label: "Contact" },
  ],
};

export default function Layout({ children }) {
  const year = new Date().getFullYear();
  const { pathname, search } = useLocation();
  const { session, logout } = useJourneyAuth();
  const isManual = pathname.startsWith("/manuals/");
  const isBeta = pathname.startsWith("/beta");
  const isHome = pathname === "/" || pathname === "/sales";
  const isDemo = pathname === "/demo" || pathname.startsWith("/demo/");
  const isPricing = pathname === "/pricing";
  const shellClass = [
    "site-shell",
    isManual ? "is-manual" : "",
    isBeta ? "is-beta" : "",
    isHome ? "is-home" : "",
    isDemo ? "is-demo" : "",
    isPricing ? "is-pricing" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={shellClass}>
      <MembershipActivationGate />
      <header className="site-header">
        <NavLink to={withFilmQuery("/", search)} className="brand" data-tour="journey-brand">
          <img src="/images/logo-mark.png" alt="" className="brand-mark" />
          <span>
            <span className="brand-name">DreamHome</span>
            <span className="brand-sub">Journey</span>
          </span>
        </NavLink>
        <nav className="top-nav main-nav" aria-label="DreamHome Journey">
          {PRIMARY.map((link) => (
            <NavLink
              key={link.to}
              to={withFilmQuery(link.to, search)}
              end={Boolean(link.end)}
              data-tour={link.to === "/support" ? "nav-support" : link.to === "/community" ? "nav-community" : undefined}
              className={({ isActive }) => `nav-link${isActive ? " is-active" : ""}`}
            >
              {link.label}
            </NavLink>
          ))}
          <a
            href={WINDOWS_APP_URL}
            className="nav-link"
            data-tour="nav-windows-app"
            title="Return to the REChecklist Windows app"
          >
            Windows App
          </a>
        </nav>
        <div className="header-actions">
          <div className="header-select-group" role="group" aria-label="Account">
            {session ? (
              <button type="button" className="header-select-item" onClick={logout}>
                Sign out
              </button>
            ) : (
              <NavLink
                to={withFilmQuery("/login", search)}
                className={({ isActive }) => `header-select-item${isActive ? " is-active" : ""}`}
              >
                Sign in
              </NavLink>
            )}
          </div>
          {session ? (
            <a
              href={deskUrl(session.role)}
              className="badge role-tag"
              title="Return to REChecklist"
            >
              REChecklist
            </a>
          ) : null}
        </div>
      </header>
      <main className={`site-main${isManual ? " is-manual" : ""}`}>{children}</main>
      <footer className="site-footer">
        <div className="footer-grid">
          {Object.entries(FOOTER).map(([title, links]) => (
            <div key={title}>
              <p className="footer-heading">{title}</p>
              {links.map((link) => (
                <NavLink key={link.to} to={withFilmQuery(link.to, search)}>
                  {link.label}
                </NavLink>
              ))}
            </div>
          ))}
          <div>
            <p className="footer-heading">Open REChecklist</p>
            <a href={appUrl("/login")}>Buyers</a>
            <a href={appUrl("/seller/login")}>Sellers</a>
            <a href={AGENT_LOGIN_URL}>Agents</a>
            <a href={BROKER_LOGIN_URL}>Brokers</a>
          </div>
        </div>
        <p className="footer-note">
          © {year} DreamHome Journey · Marketing, support, and user manuals for{" "}
          <a href="https://rechecklist.pro">REChecklist.pro</a>. Region: San Diego County · Southern
          California next. License numbers recorded on the desk are not a California DRE
          certification.
        </p>
      </footer>
    </div>
  );
}
